export type CvEntryMode = "manual" | "imported";

/**
 * Section-entry wizard for the MANUAL build path ("Build my CV manually").
 * Import/AI users review extracted content by tapping sections in the preview,
 * but a manual user has nothing to preview yet: showing them an empty CV
 * after two screens is a dead end. So while the entry mode is manual AND the
 * user has not yet reached the preview, these steps join the linear flow
 * between this-is-you and complete; once the preview has been reached they
 * revert to off-flow editors that return to it.
 *
 * The ORDER is the one the manual flow has always used: history first, then the
 * summary. The summary is derived work: it reads back the roles, studies and
 * skills the user just entered (and the AI drafts it from exactly that), so
 * asking for it first leaves the user writing an intro about nothing. Keep it
 * last.
 *
 * Certifications and highlights are deliberately NOT here: they are optional
 * sections, offered from the preview checklist rather than forced on every
 * manual user, which is how the flow behaved before the preview-first reflow.
 */
export const MANUAL_CV_SECTION_STEPS = [
  "experience",
  "education",
  "languages",
  "skills",
  "summary",
] as const;

/**
 * The contact screen (email, phone, links). An imported CV brings its own
 * contacts, so the imported flow leaves it off the linear path (it stays the
 * preview's tap-to-edit target for the header); a manual build has nothing to
 * import them from, so its wizard still asks for them before the sections.
 */
export const MANUAL_CV_CONTACT_STEP = "how-to-reach-you" as const;

/** Everything the manual wizard splices in before the preview, in order. */
export const MANUAL_CV_WIZARD_STEPS = [
  MANUAL_CV_CONTACT_STEP,
  ...MANUAL_CV_SECTION_STEPS,
] as const;

/** Insert the manual wizard (contacts, then the sections) right before the preview step. */
export function insertManualCvSections<TStep extends string>(
  flow: readonly TStep[],
): TStep[] {
  const completeIndex = flow.indexOf(OFF_FLOW_RETURN_STEP as TStep);
  if (completeIndex < 0) return [...flow];
  return [
    ...flow.slice(0, completeIndex),
    ...(MANUAL_CV_WIZARD_STEPS as readonly string[] as TStep[]).filter(
      (step) => !flow.includes(step),
    ),
    ...flow.slice(completeIndex),
  ];
}

/**
 * The onboarding flow for a claimed CV handoff (a CV someone was SENT, claimed
 * during signup with intent 'update'). The server already hydrated the new
 * account's master profile from the delivered CV, so the AI-input path and the
 * basics-before-preview order are skipped: claim on ai-input, jump straight to
 * the preview, then the two steps that still need the user (public details and
 * privacy). how-to-reach-you, where-heard and the paywall are deliberately not
 * part of this flow; completing who-sees-cv completes onboarding, exactly as
 * it does at the end of the imported and manual flows.
 */
export const CLAIMED_CV_FLOW_STEPS = [
  "ai-input",
  "complete",
  "this-is-you",
  "who-sees-cv",
] as const;

export function getClaimedCvFlowSteps<TStep extends string>(): TStep[] {
  return [...(CLAIMED_CV_FLOW_STEPS as readonly string[] as readonly TStep[])];
}

/**
 * Where an off-flow step returns to. Section editors (experience, education,
 * skills, languages, summary, certifications, highlights) and the contact
 * screen are reachable only by tapping a section in the CV preview or a
 * checklist row, so both their Back and their Save land back on the preview
 * rather than re-entering the linear flow.
 */
export const OFF_FLOW_RETURN_STEP = "complete";

/**
 * Resolve onboarding forward navigation.
 *
 * A step that is not in the flow is an editor the user opened FROM the preview,
 * so it returns there. Without this an off-flow step falls through to `flow[0]`
 * and throws the user back to the very first screen (ai-input) on save.
 */
export function resolveNextOnboardingStep<TStep extends string>(
  flow: readonly TStep[],
  activeStep: TStep,
): TStep {
  const index = flow.indexOf(activeStep);
  if (index < 0) {
    return flow.includes(OFF_FLOW_RETURN_STEP as TStep)
      ? (OFF_FLOW_RETURN_STEP as TStep)
      : flow[0]!;
  }
  return flow[Math.min(index + 1, flow.length - 1)]!;
}

/**
 * Resolve onboarding back navigation while preserving the branch the user
 * actually took. Manual CV creation never enters the CV Health check, so
 * sending those users there on Back creates a cv-health -> this-is-you loop
 * instead of returning them to the CV import screen.
 *
 * Off-flow steps mirror `resolveNextOnboardingStep`: Back returns to the
 * preview they were opened from, never to the start of the flow.
 */
export function resolvePreviousOnboardingStep<TStep extends string>(
  flow: readonly TStep[],
  activeStep: TStep,
  cvEntryMode: CvEntryMode | null,
): TStep {
  if (
    activeStep === "this-is-you" &&
    cvEntryMode === "manual" &&
    flow.includes("ai-input" as TStep)
  ) {
    return "ai-input" as TStep;
  }

  const index = flow.indexOf(activeStep);
  if (index < 0) {
    return flow.includes(OFF_FLOW_RETURN_STEP as TStep)
      ? (OFF_FLOW_RETURN_STEP as TStep)
      : flow[0]!;
  }
  return index > 0 ? flow[index - 1]! : flow[0]!;
}

/**
 * CV Health's "Fix my CV" around the soft plan chooser (choose-ai). The fix
 * is the conversion moment: tapping it on the report records a pending fix
 * and shows the chooser (the step's `nextStep`); every way out of the
 * chooser (a plan, Continue free, the X or a swipe) comes BACK to the
 * report, which then runs the fix (writing pass, fix, recheck). The chooser
 * is soft, shows once, and ONLY behind Fix: Skip, Continue after the fix, a
 * failed check and a report that skipped itself all go around it, and so
 * does Back from this-is-you. Never a second paywall.
 */
export interface CvHealthFixRouteState {
  /** The user asked for the fix; it runs when the chooser is done. */
  pendingFix: boolean;
  /** The chooser already completed on the way to the fix. */
  chooserDone: boolean;
  /** CV Health was off (or had nothing to check), so the step skipped itself: Back never lands on it. */
  skipped?: boolean;
}

/** What the CV Health step needs to know about the import to decide whether to run. */
export interface CvHealthImportLike {
  /** `file`, `text` (the composer's typed text or pasted link) or `handoff`. */
  source: string | null;
  quality: { completeness: "complete" | "minimal" | "header_only" } | null;
}

/**
 * A typed description ("just tell us what you do") that did not parse as a
 * real CV has nothing to check: the writing pass builds the CV from it, so
 * the CV Health step skips itself (the same path as the feature being off).
 * A file, a LinkedIn import or a handoff is always checked; so is typed text
 * that parsed into a full CV, and an import this session did not make (null).
 */
export function shouldSkipCvHealthForImport(importCtx: CvHealthImportLike | null): boolean {
  if (!importCtx || importCtx.source !== "text") return false;
  const completeness = importCtx.quality?.completeness;
  return completeness === "minimal" || completeness === "header_only";
}

/**
 * Forward special case, checked before `resolveNextOnboardingStep`. `null`
 * means the linear flow decides (which is what leads from the report into
 * the chooser when a fix is pending).
 */
export function resolveCvHealthNextStep<TStep extends string>(
  flow: readonly TStep[],
  activeStep: TStep,
  state: CvHealthFixRouteState,
): TStep | null {
  if (activeStep === "choose-ai" && state.pendingFix && flow.includes("cv-health" as TStep)) {
    return "cv-health" as TStep;
  }
  // Every exit of the report other than Fix (Skip, Continue after the fix,
  // Continue without checking, a report that skipped itself) goes around the
  // chooser: it is only ever shown for the fix.
  if (activeStep === "cv-health" && !state.pendingFix && flow.includes("this-is-you" as TStep)) {
    return "this-is-you" as TStep;
  }
  return null;
}

/**
 * Back special case, checked before `resolvePreviousOnboardingStep` (for an
 * imported CV; the manual build never entered the check and keeps its own
 * Back): from this-is-you, Back returns to the report, never to the chooser
 * (it shows once, behind Fix). When CV Health skipped itself (feature off,
 * nothing to check), Back goes around the report too: it would only skip
 * forward again.
 */
export function resolveCvHealthPreviousStep<TStep extends string>(
  flow: readonly TStep[],
  activeStep: TStep,
  state: CvHealthFixRouteState,
): TStep | null {
  const health = flow.indexOf("cv-health" as TStep);
  if (health < 0) return null;
  // The step before the report (and so before the chooser that follows it).
  const beforeHealth = health > 0 ? flow[health - 1]! : null;
  if (activeStep === "this-is-you") {
    return state.skipped ? beforeHealth : ("cv-health" as TStep);
  }
  if (activeStep === "choose-ai" && state.skipped) return beforeHealth;
  return null;
}

/**
 * The browser history entries the web wizard wrote this session, newest
 * last. It lets in-app Back step back through the real history (no duplicate
 * entry, so the next browser Back is never a dead tap) when the entry behind
 * is the step Back wants. Only what is certain is kept: anything the wizard
 * did not write (browser Forward, a typed URL, a reload) leaves just the
 * current entry, and Back falls back to replacing it.
 */
export function reconcileWebStepHistory<TStep extends string>(
  history: readonly TStep[],
  step: TStep | null,
): TStep[] {
  if (!step) return [...history];
  if (history[history.length - 1] === step) return [...history];
  // Browser Back by one entry.
  if (history.length >= 2 && history[history.length - 2] === step) {
    return history.slice(0, -1);
  }
  return [step];
}

export function recordWebStepNavigation<TStep extends string>(
  history: readonly TStep[],
  target: TStep,
  mode: "push" | "replace",
): TStep[] {
  return mode === "push" ? [...history, target] : [...history.slice(0, -1), target];
}

/** True when the history entry behind the current one is `target`. */
export function canReturnByHistoryBack<TStep extends string>(
  history: readonly TStep[],
  target: TStep,
): boolean {
  return history.length >= 2 && history[history.length - 2] === target;
}
